import {
  PagedRequest,
  PagedResponse,
  IProduct,
  ProductScope,
} from '@topshots/core-models';
import { topshotsAPI } from '../../app/api';

const productsApi = topshotsAPI.injectEndpoints({
  endpoints: (build) => ({
    getProducts: build.query<
      PagedResponse<IProduct[]>,
      PagedRequest & {
        search?: string;
        includeArchived?: boolean;
        includeUnconfirmed?: boolean;
        scope?: ProductScope;
      }
    >({
      query: (request) => ({
        url: 'products',
        method: 'GET',
        params: request,
      }),
      providesTags: (result, error, req) =>
        result
          ? [
              // Provides a tag for each enquiry in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.data.map(({ _id }) => ({
                type: 'Products' as const,
                id: _id.toString(),
              })),
              {
                type: 'Products',
                id: `PARTIAL-LIST`,
              },
            ]
          : [
              {
                type: 'Products',
                id: `PARTIAL-LIST`,
              },
            ],
    }),
    getProduct: build.query<IProduct, string>({
      query: (id) => ({
        url: `products/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, page) => [
        { type: 'Products' as const, id: result?._id.toString() },
      ],
    }),
    getProductAddOns: build.query<IProduct[], string>({
      query: (id) => ({
        url: `products/${id}/addons`,
        method: 'GET',
      }),
      providesTags: (result, error, page) =>
        result
          ? [
              // Provides a tag for each enquiry in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.map(({ _id }) => ({
                type: 'Products' as const,
                id: _id.toString(),
              })),
              { type: 'Products', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Products', id: 'PARTIAL-LIST' }],
    }),
    updateProduct: build.mutation<
      IProduct,
      { id: string; data: Partial<IProduct> }
    >({
      query: (request) => ({
        url: `products/${request.id}`,
        method: 'PUT',
        body: request.data,
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Products', id: 'PARTIAL-LIST' },
        { type: 'Products', id: product.id },
      ],
    }),
    createProduct: build.mutation<IProduct, Partial<IProduct>>({
      query: (request) => ({
        url: `products`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Products', id: 'PARTIAL-LIST' },
        { type: 'Products', id: result?._id.toString() },
      ],
    }),
    duplicateProduct: build.mutation<IProduct, string>({
      query: (id) => ({
        url: `products/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Products', id: 'PARTIAL-LIST' },
        { type: 'Products', id: result?._id.toString() },
      ],
    }),
    deleteProduct: build.mutation<IProduct, string>({
      query: (id) => ({
        url: `products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Products', id: 'PARTIAL-LIST' },
        { type: 'Products', id: result?._id.toString() },
      ],
    }),
    deleteAddOn: build.mutation<
      IProduct,
      { productId: string; addOnId: string }
    >({
      query: (req) => ({
        url: `products/${req.productId}/addons/${req.addOnId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Products', id: 'PARTIAL-LIST' },
        { type: 'Products', id: result?._id.toString() },
      ],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductQuery,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useCreateProductMutation,
  useGetProductAddOnsQuery,
  useDeleteAddOnMutation,
  useDuplicateProductMutation,
} = productsApi;
